import { BaseTimePeriod, TimePeriod } from '@services/FiltersTypes';
import { isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import { Periodicity, PeriodicityKey } from '@components/time-period-selector/PeriodsTypes';
import { PageName } from '@utils/pageConfig/Types';

export const MAT_YTD_PREFIX_LENGTH: number = 4;

// Controls the latest n periods to display for weekly and monthly period options
const WEEKLY_LATEST: number = 5;
const MONTHLY_LATEST: number = 3;

export const getLatestTopNPeriodOptions: (periodOptions: BaseTimePeriod[], latestTopN: number) => BaseTimePeriod[] = (
  periodOptions,
  latestTopN,
) => {
  // Assumption: the time periods retrieved from the backend is sorted in ascending order.
  // Breaking this would require sorting the filtered periodOptions
  //
  // Decided against directly accessing last 2 elements in favour of filtering the list in
  // anticipation if future backend changes returns unsorted periodOptions
  const filteredPeriodOptions = periodOptions.filter((periodOption) => !isPeriodYTDOrMAT(periodOption));
  const lastIndex = filteredPeriodOptions.length;
  return filteredPeriodOptions.slice(lastIndex - latestTopN, lastIndex).reverse();
};

export const formatYTDorMATLabel: (ytdMATPeriodOptions: BaseTimePeriod[]) => BaseTimePeriod[] = (
  ytdMATPeriodOptions,
) => {
  // Assumption: ytdMATPeriodOptions should only contain YTD and MAT period options
  return ytdMATPeriodOptions.map((option) => ({
    ...option,
    label: option.label.slice(MAT_YTD_PREFIX_LENGTH),
  }));
};

export const getYTDOrMATPeriodOptions: (
  periodOptions: TimePeriod,
  periodicity: Periodicity.MAT | Periodicity.YTD,
) => BaseTimePeriod[] = (periodOptions, periodicity) => {
  // Assumption: the built period label for both YTD and MAT contains these keywords, which is the
  // current behaviour returned from the backend
  const searchPeriodicity: string = periodicity === Periodicity.YTD ? 'YTD' : 'MAT';

  const monthly: BaseTimePeriod[] = periodOptions.monthly.filter((periodOption) =>
    periodOption.label.includes(searchPeriodicity),
  );
  const weekly: BaseTimePeriod[] = periodOptions.weekly.filter((periodOption) =>
    periodOption.label.includes(searchPeriodicity),
  );

  // Include in return value if the searched period option is available
  const retMonthlyAndWeeklyPeriods: BaseTimePeriod[] = [];
  if (monthly) {
    retMonthlyAndWeeklyPeriods.push(...monthly);
  }
  if (weekly) {
    retMonthlyAndWeeklyPeriods.push(...weekly);
  }

  return formatYTDorMATLabel(retMonthlyAndWeeklyPeriods);
};

export const periodOptionsCallback: (
  periodOptions: TimePeriod,
  pageName: PageName,
) => (periodicity: Periodicity) => BaseTimePeriod[] = (periodOptions, pageName) => (periodicity) => {
  let baseTimePeriodOptions: BaseTimePeriod[];
  switch (periodicity) {
    case Periodicity.Weekly:
      baseTimePeriodOptions = getLatestTopNPeriodOptions(periodOptions.weekly, WEEKLY_LATEST);
      break;
    case Periodicity.Monthly:
      baseTimePeriodOptions = getLatestTopNPeriodOptions(periodOptions.monthly, MONTHLY_LATEST);
      break;
    case Periodicity.YTD:
      baseTimePeriodOptions = getYTDOrMATPeriodOptions(periodOptions, Periodicity.YTD);
      break;
    case Periodicity.MAT:
      baseTimePeriodOptions = getYTDOrMATPeriodOptions(periodOptions, Periodicity.MAT);
      break;
    case Periodicity.RollingQuarter:
      baseTimePeriodOptions = [];
      if (isValidPageToDisplayPeriod(pageName)) {
        baseTimePeriodOptions = periodOptions.rollingQuarter;
      }
      break;
    case Periodicity.CalendarQuarter:
      baseTimePeriodOptions = [];
      if (isValidPageToDisplayPeriod(pageName)) {
        baseTimePeriodOptions = periodOptions.calendarQuarter;
      }
      break;
    case Periodicity.CustomMonths:
      baseTimePeriodOptions = [];
      if (periodOptions?.customMonths) {
        baseTimePeriodOptions = periodOptions.customMonths;
      }
      break;
    case Periodicity.CustomWeeks:
      baseTimePeriodOptions = [];
      if (periodOptions?.customWeeks) {
        baseTimePeriodOptions = periodOptions.customWeeks;
      }
      break;
    default:
      // Display no options, i.e. unsupported periodicity was supplied
      baseTimePeriodOptions = [];
      break;
  }

  return baseTimePeriodOptions;
};

export const hasMATOrYTDPeriodicity: (
  periodOptions: TimePeriod,
  periodicity: Periodicity.MAT | Periodicity.YTD,
) => boolean = (periodOptions, periodicity) => {
  // Assumption: the built period label for both YTD and MAT contains these keywords, which is the
  // current behaviour returned from the backend
  const searchPeriodicity: string = periodicity === Periodicity.YTD ? 'YTD' : 'MAT';

  const hasWeeklyYTDOrMAT = periodOptions?.weekly.some((option) => option.value.includes(searchPeriodicity));
  const hasMonthlyYTDOrMAT = periodOptions?.monthly.some((option) => option.value.includes(searchPeriodicity));

  return hasWeeklyYTDOrMAT || hasMonthlyYTDOrMAT;
};

export const isValidPageToDisplayPeriod: (pageName: PageName) => boolean = (pageName: PageName) => {
  return [
    PageName.CATEGORY_LEADERBOARD,
    PageName.PERFORMANCE_ANALYSIS,
    PageName.OPPORTUNITY_REVIEW,
    PageName.EXPLORE,
    PageName.TRADING_ASSISTANT,
  ].includes(pageName);
};

export const checkMATOrYTDPeriodicity: (period: BaseTimePeriod) => boolean = (period) => {
  //Check whether its YTD or MAT periodicity by checking from the period options selected
  return period.value.includes(PeriodicityKey.YTD) || period.value.includes(PeriodicityKey.MAT);
};
